import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { catchError, defer, delay, EMPTY, exhaustMap, filter, from, iif, map, of, switchMap, tap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { PfAngularOauth2OidcService } from '@bnpp-pf/pf-angular-oauth2-oidc';
import { LoaderComponent, NoValue, UserInfos } from '@tribeauto/ngx-mobilite-material';
import { UserService } from './user.service';
import { ApplicationStore } from '../../core/services/application-store.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AutoLogOutService } from '../authentication/autologout.service';
import { Router } from '@angular/router';
import { ThemingService } from '../../core/services/theming/theming.service';
import { CallbackStore } from '../pages/callback/callback-store.service';
import { RedirectQueryParams } from '../pages/callback/callback.model';
import { AppConfig } from '../../app.config';

@Component({
  selector: 'pvmo-user-connection',
  templateUrl: './user-connection.component.html',
  styleUrl: './user-connection.component.scss',
  imports: [MatIconModule, LoaderComponent],
  standalone: true
})
export class UserConnectionComponent implements OnInit {
  private readonly authService = inject(PfAngularOauth2OidcService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly oauthService = inject(OAuthService);
  private readonly userService = inject(UserService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly autoLogOutService = inject(AutoLogOutService);
  private readonly router = inject(Router);
  private readonly callbackStore = inject(CallbackStore);
  private readonly themingService = inject(ThemingService);
  private readonly appConfig = inject(AppConfig);

  oidcUser = toSignal(this.authService.$user);
  pvmoUser = this.applicationStore.instant('userData');
  userDisconnected = false;
  isUserConnected = this.userService.isUserConnected;

  ngOnInit(): void {
    from(this.authService.loadDiscoveryDocumentAndTryLogin())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        catchError(() => {
          void this.router.navigateByUrl('/error');
          this.userDisconnected = true;
          return of(null);
        }),
        switchMap(() => {
          const user = this.oidcUser();
          if (!user) {
            this.login();
            return of(null);
          }
          return iif(
            () => this.appConfig.devOrLocal,
            this.userService.getUserByLogin(user.sub),
            this.userService.getUserInfos()
          ).pipe(
            catchError(() => {
              void this.router.navigateByUrl('/error');
              return of(null);
            })
          );
        }),
        switchMap((userInfos: UserInfos | null) => {
          if (!userInfos) {
            return of(null);
          }
          return defer(() => {
            if (this.isLastConnectionDateBeforeToday(userInfos.lastConnectionDate)) {
              return this.userService.updateUserLastConnectionDate(userInfos.id);
            } else {
              return of(userInfos);
            }
          }).pipe(
            catchError(() => {
              void this.router.navigateByUrl('/error');
              return of(null);
            })
          );
        }),
        tap(userData => {
          this.applicationStore.update('userData', userData);
          if (userData) {
            this.autoLogOutService.startMonitoring(() => this.authService.logout());
          }
        }),
        filter(userData => !!userData),
        filter(userData => !!userData?.pointOfSales?.[0]?.tvei),
        map(userData => userData.pointOfSales[0].tvei),
        switchMap(tvei => {
          return this.themingService.getThemeFromPoS(tvei).pipe(
            catchError(() => {
              return of(null);
            })
          );
        })
      )
      .subscribe();

    this.oauthService.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event.type === 'logout' || event.type === 'token_expires'),
        exhaustMap(oauthEvent => {
          if (oauthEvent.type === 'logout') {
            this.login();
            return EMPTY;
          }
          return from(this.oauthService.refreshToken()).pipe(
            delay(200),
            catchError(() => {
              this.userDisconnected = true;
              this.applicationStore.update('userData', null);
              void this.router.navigate(['/error'], { queryParams: { tokenExpired: true } });
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }

  login(): void {
    if (this.authService.hasProblemWithServiceWorker()) {
      void this.router.navigateByUrl('/error');
      return;
    }
    const redirectModel = this.callbackStore.value();
    const login_hint = redirectModel?.queryParams?.login_hint;
    let params: RedirectQueryParams = {};
    if (login_hint) {
      params = { login_hint };
    }
    this.authService.login(JSON.stringify(redirectModel), params);
  }

  private isLastConnectionDateBeforeToday(lastConnectionDate: Date | NoValue): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return lastConnectionDate ? new Date(lastConnectionDate) < today : true;
  }
}
